import { useState, useEffect, useRef } from 'react';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useNavigate } from 'react-router-dom';
import LoginModal from '../home/components/LoginModal';
import UpgradeTrialModal from '../../components/feature/UpgradeTrialModal';
import Breadcrumb from '../../components/feature/Breadcrumb';
import ToolsDropdown from '../../components/feature/ToolsDropdown';
import OnboardingTour from './components/OnboardingTour';
import ComparisonView from './components/ComparisonView';
import PromptHistoryDropdown, { savePromptToHistory } from './components/PromptHistoryDropdown';
import UpscalePanel from './components/UpscalePanel';
import KeyboardShortcutsLegend from './components/KeyboardShortcutsLegend';
import StyleTransferPanel, { StyleTransferConfig } from './components/StyleTransferPanel';

const STYLES = [
  'Realistic', 'Anime', 'Digital Art', 'Oil Painting',
  'Watercolor', 'Sketch', '3D Render', 'Pixel Art'
];

const ASPECT_RATIOS = [
  { label: '1:1', value: '1:1' },
  { label: '16:9', value: '16:9' },
  { label: '9:16', value: '9:16' },
  { label: '4:3', value: '4:3' }
];

const QUALITY_OPTIONS = ['Standard', 'HD', 'Ultra HD'];

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const MAX_FILES = 10;
const FREE_TRIAL_LIMIT = 3;
const FREE_TRIAL_KEY = 'ai-image-free-uses';
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
const ACCEPTED_DOC_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv'
];

interface AttachedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  preview?: string;
}

interface GenerationHistory {
  id: string;
  prompt: string;
  imageUrl: string;
  style: string;
  aspectRatio: string;
  quality: string;
  timestamp: number;
}

const EXAMPLE_PROMPTS: Record<string, string[]> = {
  'Realistic': [
    'A majestic lion resting on a golden savanna at sunset, photorealistic, dramatic lighting',
    'Aerial view of a dense rainforest with morning mist, ultra-detailed, 8K resolution',
    'A cozy coffee shop interior with warm lighting, wooden furniture, and rain outside the window'
  ],
  'Anime': [
    'A young warrior standing on a cliff overlooking a futuristic city, anime style, vibrant colors',
    'Cherry blossom festival scene with lanterns and traditional clothing, Studio Ghibli inspired',
    'A magical academy library with floating books and glowing runes, detailed anime illustration'
  ],
  'Digital Art': [
    'Cyberpunk cityscape at night with neon reflections on wet streets, digital painting',
    'A fantasy dragon soaring above ancient ruins, epic digital art, cinematic composition',
    'Abstract cosmic landscape with nebulae and alien planets, vibrant digital artwork'
  ],
  'Oil Painting': [
    'Portrait of a Victorian-era woman in a garden, impressionist oil painting style',
    'Stormy sea with a sailing ship, dramatic oil painting, Rembrandt lighting',
    'Autumn forest path with golden leaves, classical oil painting technique'
  ]
};

const generationSteps = [
  'Analyzing your prompt...',
  'Initializing AI model...',
  'Generating composition...',
  'Applying style and details...',
  'Enhancing quality...',
  'Finalizing image...'
];

export default function AIImageGenerator() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('Realistic');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [quality, setQuality] = useState('Standard');
  const [generatedImage, setGeneratedImage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<GenerationHistory[]>([]);
  const [darkMode, toggleDarkMode] = useDarkMode();
  const setDarkMode = (v: boolean) => { if (v !== darkMode) toggleDarkMode(); };
  const [showShareModal, setShowShareModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [activeExampleCategory, setActiveExampleCategory] = useState('Realistic');
  const [freeUsesCount, setFreeUsesCount] = useState(() => {
    try { return parseInt(localStorage.getItem(FREE_TRIAL_KEY) || '0', 10); } catch { return 0; }
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const trialExhausted = freeUsesCount >= FREE_TRIAL_LIMIT;
  const trialRemaining = Math.max(0, FREE_TRIAL_LIMIT - freeUsesCount);

  // Enhance prompt
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [originalPrompt, setOriginalPrompt] = useState('');

  // Comparison view
  const [generatedImageB, setGeneratedImageB] = useState('');
  const [promptB, setPromptB] = useState('');
  const [isGeneratingB, setIsGeneratingB] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  // Keyboard shortcuts overlay
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Upscale panel
  const [showUpscale, setShowUpscale] = useState(false);

  // Style transfer
  const [styleTransferConfig, setStyleTransferConfig] = useState<StyleTransferConfig | null>(null);
  const [lastUsedStyleTransfer, setLastUsedStyleTransfer] = useState<StyleTransferConfig | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem('ai-image-generator-history');
      if (savedHistory) setHistory(JSON.parse(savedHistory));
    } catch (e) {
      console.error('Failed to load history:', e);
    }
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const ctrl = e.ctrlKey || e.metaKey;
      const tag = (e.target as HTMLElement)?.tagName;
      const inInput = tag === 'TEXTAREA' || tag === 'INPUT';

      if (e.key === '?' && !inInput) { e.preventDefault(); setShowShortcuts(v => !v); return; }
      if (e.key === 'Escape') { setShowShortcuts(false); setShowComparison(false); setShowHistory(false); return; }
      if (!ctrl) return;

      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleGenerate(); return; }
      if (e.key === 'e' || e.key === 'E') { e.preventDefault(); enhancePrompt(); return; }
      if (e.key === 'k' || e.key === 'K') { e.preventDefault(); generateVariation(); return; }
      if (e.key === 'h' || e.key === 'H') { e.preventDefault(); setShowHistory(v => !v); return; }
      if (e.key === 's' || e.key === 'S') { e.preventDefault(); if (generatedImage && !trialExhausted) setShowShareModal(true); return; }
      if (e.key === 'd' || e.key === 'D') { e.preventDefault(); if (generatedImage && !trialExhausted) showToastMsg('Image downloaded!'); return; }
      if (e.key === 'l' || e.key === 'L') { e.preventDefault(); setPrompt(''); setOriginalPrompt(''); return; }
      if (e.key === 'z' || e.key === 'Z') { if (originalPrompt) { e.preventDefault(); setPrompt(originalPrompt); setOriginalPrompt(''); } return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt, generatedImage, trialExhausted, originalPrompt, isGenerating, isEnhancing]);

  const saveToHistory = (newGeneration: GenerationHistory) => {
    const updatedHistory = [newGeneration, ...history].slice(0, 20);
    setHistory(updatedHistory);
    localStorage.setItem('ai-image-generator-history', JSON.stringify(updatedHistory));
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('ai-image-generator-history');
  };

  const removeHistoryItem = (id: string) => {
    const updatedHistory = history.filter(item => item.id !== id);
    setHistory(updatedHistory);
    localStorage.setItem('ai-image-generator-history', JSON.stringify(updatedHistory));
  };

  const restoreFromHistory = (item: GenerationHistory) => {
    setPrompt(item.prompt);
    setSelectedStyle(item.style);
    setAspectRatio(item.aspectRatio);
    setQuality(item.quality);
    setGeneratedImage(item.imageUrl);
    setShowHistory(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatTimestamp = (timestamp: number) => {
    const diffMs = Date.now() - timestamp;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const truncateText = (text: string, maxLength: number) =>
    text.length <= maxLength ? text : `${text.slice(0, maxLength)}...`;

  /* ---------- File handling ---------- */
  const handleFiles = (files: FileList) => {
    setFileError('');
    const newFiles: AttachedFile[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (attachedFiles.length + newFiles.length >= MAX_FILES) {
        setFileError(`Maximum ${MAX_FILES} files allowed`);
        break;
      }
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`${file.name} exceeds 25MB limit`);
        continue;
      }
      const allAccepted = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES, ...ACCEPTED_DOC_TYPES];
      if (!allAccepted.includes(file.type)) {
        setFileError(`${file.name} is not a supported file type`);
        continue;
      }
      const fileObj: AttachedFile = {
        id: `${Date.now()}-${i}`,
        name: file.name,
        size: file.size,
        type: file.type,
      };
      if (ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setAttachedFiles(prev =>
            prev.map(f => f.id === fileObj.id ? { ...f, preview: e.target?.result as string } : f)
          );
        };
        reader.readAsDataURL(file);
      }
      newFiles.push(fileObj);
    }
    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (id: string) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== id));
    setFileError('');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files);
  };

  /* ---------- Enhance Prompt ---------- */
  const ENHANCE_SUFFIXES: Record<string, string> = {
    'Realistic': ', ultra-photorealistic, 8K resolution, dramatic cinematic lighting, shallow depth of field, shot on Sony A7R IV, golden hour, rich textures, highly detailed',
    'Anime': ', vibrant anime illustration, Studio Ghibli inspired, soft cel shading, expressive linework, dynamic composition, lush color palette, cinematic framing',
    'Digital Art': ', stunning digital painting, concept art quality, intricate details, painterly brushstrokes, dramatic perspective, ArtStation trending, luminous atmosphere',
    'Oil Painting': ', masterful oil painting technique, rich impasto texture, classical composition, Rembrandt chiaroscuro lighting, museum quality, rich color palette',
    'Watercolor': ', delicate watercolor washes, soft luminous hues, fluid paint bleeds, white paper highlights, loose expressive strokes, fine art quality',
    'Sketch': ', detailed pencil sketch, fine cross-hatching, expressive gestural lines, professional illustration, precise proportions, dramatic contrast',
    '3D Render': ', photorealistic 3D render, Octane Render, subsurface scattering, physically based materials, cinematic camera, ultra-crisp details, studio lighting rig',
    'Pixel Art': ', vibrant pixel art, meticulously crafted sprites, rich color palette, retro game aesthetic, sharp pixel grid, nostalgic 16-bit style',
  };

  const enhancePrompt = async () => {
    if (!prompt.trim() || isEnhancing) return;
    setIsEnhancing(true);
    setOriginalPrompt(prompt);
    await new Promise(r => setTimeout(r, 1400));
    const suffix = ENHANCE_SUFFIXES[selectedStyle] || ', highly detailed, professional quality, masterful composition, stunning visual impact';
    // Build an enhanced version: capitalise first letter, remove trailing period, append suffix
    const cleaned = prompt.trim().replace(/\.$/, '');
    const enhanced = cleaned.charAt(0).toUpperCase() + cleaned.slice(1) + suffix;
    setPrompt(enhanced);
    setIsEnhancing(false);
  };

  /* ---------- Generate Variation (Comparison) ---------- */
  const generateVariation = async () => {
    if (!generatedImage || trialExhausted) {
      if (trialExhausted) { setShowUpgradeModal(true); }
      return;
    }
    setIsGeneratingB(true);
    // Slightly mutated prompt for variation
    const variationSuffix = ', alternative composition, different lighting angle, unique mood variation';
    const variationPrompt = `${selectedStyle} style: ${prompt}${variationSuffix}`;
    const pB = `${prompt} (variation)`;
    setPromptB(pB);
    await new Promise(r => setTimeout(r, 3200));
    const width = aspectRatio === '16:9' ? 1024 : aspectRatio === '9:16' ? 576 : 1024;
    const height = aspectRatio === '16:9' ? 576 : aspectRatio === '9:16' ? 1024 : aspectRatio === '4:3' ? 768 : 1024;
    const orientation = aspectRatio === '16:9' ? 'landscape' : aspectRatio === '9:16' ? 'portrait' : 'squarish';
    const imgB = `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg${encodeURIComponent(variationPrompt + ' variant unique')}&width=${width}&height=${height}&seq=cmp-${Date.now()}&orientation=${orientation}`;
    setGeneratedImageB(imgB);
    setIsGeneratingB(false);
    setShowComparison(true);
  };

  /* ---------- Generation ---------- */
  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    // Gate behind upgrade modal when free trial is exhausted
    if (trialExhausted) {
      setShowUpgradeModal(true);
      return;
    }
    setIsGenerating(true);
    setCurrentStep(0);
    setGeneratedImage('');

    for (let i = 0; i < generationSteps.length; i++) {
      setCurrentStep(i);
      await new Promise(resolve => setTimeout(resolve, 1200));
    }

    const styleAddition = styleTransferConfig ? `, ${styleTransferConfig.promptAddition}` : '';
    const imagePrompt = `${selectedStyle} style: ${prompt}${styleAddition}`;
    const width = aspectRatio === '16:9' ? 1024 : aspectRatio === '9:16' ? 576 : aspectRatio === '4:3' ? 1024 : 1024;
    const height = aspectRatio === '16:9' ? 576 : aspectRatio === '9:16' ? 1024 : aspectRatio === '4:3' ? 768 : 1024;
    const orientation = aspectRatio === '16:9' ? 'landscape' : aspectRatio === '9:16' ? 'portrait' : 'squarish';
    const imageUrl = `https://readdy.ai/api/search-image?query=${encodeURIComponent(imagePrompt)}&width=${width}&height=${height}&seq=ai-gen-${Date.now()}&orientation=${orientation}`;

    setGeneratedImage(imageUrl);
    setIsGenerating(false);
    setLastUsedStyleTransfer(styleTransferConfig);

    // Increment free trial counter
    const newCount = freeUsesCount + 1;
    setFreeUsesCount(newCount);
    try { localStorage.setItem(FREE_TRIAL_KEY, String(newCount)); } catch { /* noop */ }

    // Save to prompt history
    savePromptToHistory(prompt);

    saveToHistory({
      id: `gen-${Date.now()}`,
      prompt: prompt.trim(),
      imageUrl,
      style: selectedStyle,
      aspectRatio,
      quality,
      timestamp: Date.now()
    });
  };

  /* ---------- Share / clipboard ---------- */
  const showToastMsg = (msg: string) => {
    setToastMessage(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const copyToClipboard = (text: string, message: string) => {
    navigator.clipboard.writeText(text).then(() => showToastMsg(message));
  };

  const handleCopyImageLink = () => copyToClipboard(generatedImage, 'Image link copied to clipboard!');
  const handleCopyPrompt = () => copyToClipboard(prompt, 'Prompt copied to clipboard!');
  const handleCopyEmbedCode = () => {
    copyToClipboard(`<img src="${generatedImage}" alt="${prompt}" />`, 'Embed code copied to clipboard!');
  };

  const handleDownloadPNG = () => {
    setShowShareModal(false);
    if (trialExhausted) { setShowUpgradeModal(true); return; }
    showToastMsg('PNG download started!');
  };
  const handleDownloadJPG = () => {
    setShowShareModal(false);
    if (trialExhausted) { setShowUpgradeModal(true); return; }
    showToastMsg('JPG download started!');
  };

  const handleSocialShare = (platform: string) => {
    const text = encodeURIComponent(`Check out this AI-generated image: ${prompt}`);
    const url = encodeURIComponent(generatedImage);
    const urls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${url}&description=${text}`
    };
    if (urls[platform]) window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  /* ---------- Render ---------- */
  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
    }`}>
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 dark:bg-gray-900 transition-colors duration-300">

        {/* Navbar */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          darkMode 
            ? 'bg-gray-900/80 backdrop-blur-md border-b border-white/10' 
            : 'bg-white/80 backdrop-blur-md border-b border-gray-200'
        }`}>
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <button onClick={() => navigate('/')} className="cursor-pointer flex-shrink-0">
                <div className="bg-white rounded-xl px-2 py-1">
                  <img
                    src="https://static.readdy.ai/image/0de80e9fd237f0b8df79ee5619c3a653/49cc0aa73e52e5f4616af3a61a585458.jpeg"
                    alt="1WORLD-AI Logo"
                    className="h-11 w-auto object-contain"
                  />
                </div>
              </button>
              <div className="flex items-center gap-3">
                <ToolsDropdown isDarkMode={darkMode} />
                <button
                  onClick={() => setShowShortcuts(true)}
                  className="p-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Keyboard shortcuts (?)"
                >
                  <i className="ri-keyboard-line text-xl text-gray-700 dark:text-gray-300"></i>
                </button>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className="p-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  <i className={`${darkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl text-gray-700 dark:text-gray-300`}></i>
                </button>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="relative px-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-500 to-pink-600 text-white font-medium hover:shadow-lg hover:shadow-purple-500/30 transition-all whitespace-nowrap"
                >
                  <i className="ri-history-line mr-2"></i>
                  My Generations
                  {history.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                      {history.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Breadcrumb */}
        <div className="fixed top-16 left-0 right-0 z-40">
          <Breadcrumb currentPage="AI Image Generator" variant={darkMode ? 'dark' : 'light'} />
        </div>

        {/* Main Content */}
        <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">

            {/* Hero */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full text-sm font-medium mb-6">
                <div className="relative">
                  <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full animate-ping opacity-75"></div>
                </div>
                <span className="font-semibold">OpenClaw Vision Engine</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 text-xs font-semibold mb-6 ml-3">
                <i className="ri-checkbox-circle-fill"></i>
                <span>50M+ Images Created</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                Create Stunning Images
                <span className="block bg-gradient-to-r from-purple-500 to-pink-600 bg-clip-text text-transparent">
                  From Text Prompts
                </span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Generate high-resolution, professional-quality images powered by OpenClaw AI
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">

                {/* Prompt Input */}
                <div data-tour="prompt" className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-gray-900 dark:text-white">
                      <i className="ri-edit-line mr-2 text-purple-500"></i>
                      Describe Your Image
                    </label>
                    <PromptHistoryDropdown
                      onSelect={(p) => { setPrompt(p); setOriginalPrompt(''); }}
                      darkMode={darkMode}
                    />
                  </div>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value.slice(0, 800))}
                    placeholder="Describe the image you want to create in detail. Include subjects, style, lighting, mood, and any specific elements..."
                    className="w-full h-32 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl p-3 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"
                  />
                  <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                    <span className="text-xs text-gray-400 dark:text-gray-500">{prompt.length} / 800</span>
                    <div className="flex items-center gap-2">
                      {originalPrompt && !isEnhancing && (
                        <button
                          onClick={() => { setPrompt(originalPrompt); setOriginalPrompt(''); }}
                          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          <i className="ri-arrow-go-back-line mr-1"></i>Undo enhance
                        </button>
                      )}
                      {prompt.length > 0 && (
                        <button onClick={() => { setPrompt(''); setOriginalPrompt(''); }} className="text-xs text-purple-600 dark:text-purple-400 hover:underline font-medium">Clear</button>
                      )}
                    </div>
                  </div>
                  {/* Enhance Prompt button */}
                  <button
                    onClick={enhancePrompt}
                    disabled={!prompt.trim() || isEnhancing}
                    className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 text-sm font-semibold hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {isEnhancing ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        <span>Enhancing your prompt...</span>
                        <span className="flex gap-0.5">
                          {[0, 1, 2].map(i => (
                            <span key={i} className="w-1 h-1 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                          ))}
                        </span>
                      </>
                    ) : (
                      <>
                        <i className="ri-sparkling-fill text-base"></i>
                        <span>✨ Enhance Prompt</span>
                        <span className="text-xs font-normal text-purple-400 dark:text-purple-500">AI upgrades your description</span>
                      </>
                    )}
                  </button>
                  {originalPrompt && !isEnhancing && (
                    <div className="mt-2 p-2.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-xs text-green-700 dark:text-green-400 flex items-start gap-2">
                      <i className="ri-check-circle-line flex-shrink-0 mt-0.5"></i>
                      <span>Prompt enhanced! Added style-specific details for better results. Click "Undo enhance" to revert.</span>
                    </div>
                  )}
                </div>

                {/* Style */}
                <div data-tour="style" className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                  <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-4">
                    <i className="ri-palette-line mr-2 text-purple-500"></i>
                    Art Style
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {STYLES.map((s) => (
                      <button
                        key={s}
                        onClick={() => setSelectedStyle(s)}
                        className={`px-3 py-2 rounded-xl text-xs font-medium transition-all border-2 ${
                          selectedStyle === s
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
                            : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:border-purple-300'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Style Transfer */}
                <StyleTransferPanel
                  darkMode={darkMode}
                  activeConfig={styleTransferConfig}
                  onConfigChange={setStyleTransferConfig}
                />

                {/* Aspect Ratio & Quality */}
                <div data-tour="aspect" className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                        <i className="ri-crop-line mr-2 text-purple-500"></i>
                        Aspect Ratio
                      </label>
                      <div className="space-y-2">
                        {ASPECT_RATIOS.map((ar) => (
                          <button
                            key={ar.value}
                            onClick={() => setAspectRatio(ar.value)}
                            className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                              aspectRatio === ar.value
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
                                : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:border-purple-300'
                            }`}
                          >
                            {ar.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                        <i className="ri-hd-line mr-2 text-purple-500"></i>
                        Quality
                      </label>
                      <div className="space-y-2">
                        {QUALITY_OPTIONS.map((q) => (
                          <button
                            key={q}
                            onClick={() => setQuality(q)}
                            className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                              quality === q
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
                                : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:border-purple-300'
                            }`}
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reference Files */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setShowAttachments(!showAttachments)}
                    className="w-full flex items-center justify-between text-sm font-semibold text-gray-900 dark:text-white"
                  >
                    <span className="flex items-center gap-2">
                      <i className="ri-attachment-line text-purple-500"></i>
                      Reference Files
                      {attachedFiles.length > 0 && (
                        <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-xs rounded-full">
                          {attachedFiles.length}
                        </span>
                      )}
                    </span>
                    <i className={`ri-arrow-${showAttachments ? 'up' : 'down'}-s-line text-gray-400`}></i>
                  </button>

                  {showAttachments && (
                    <div className="mt-4 space-y-4">
                      <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                          isDragging
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 hover:border-purple-400'
                        }`}
                      >
                        <i className="ri-upload-cloud-line text-4xl text-purple-500 mb-3 block"></i>
                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Drop files here or click to browse</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Images, videos, or documents (max 25MB each, up to 10 files)</p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*,video/*,.pdf,.doc,.docx,.txt,.csv"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      {fileError && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <p className="text-sm text-red-600 dark:text-red-400">
                            <i className="ri-error-warning-line mr-1"></i>{fileError}
                          </p>
                        </div>
                      )}
                      {attachedFiles.length > 0 && (
                        <div className="space-y-3">
                          {attachedFiles.filter(f => f.type.startsWith('image/')).length > 0 && (
                            <div className="grid grid-cols-4 gap-2">
                              {attachedFiles.filter(f => f.type.startsWith('image/')).map((file) => (
                                <div key={file.id} className="relative group aspect-square">
                                  {file.preview && (
                                    <img src={file.preview} alt={file.name} className="w-full h-full object-cover rounded-lg" />
                                  )}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); removeFile(file.id); }}
                                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                  >
                                    <i className="ri-close-line text-xs"></i>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {attachedFiles.filter(f => !f.type.startsWith('image/')).map((file) => (
                            <div key={file.id} className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-900 rounded-lg">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <i className={`${file.type.startsWith('video/') ? 'ri-video-line' : 'ri-file-text-line'} text-xl text-purple-500`}></i>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                </div>
                              </div>
                              <button onClick={() => removeFile(file.id)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                                <i className="ri-delete-bin-6-line"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Free trial indicator */}
                {!trialExhausted ? (
                  <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium ${
                    trialRemaining === 1
                      ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400'
                      : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                  }`}>
                    <div className="flex gap-1">
                      {Array.from({ length: FREE_TRIAL_LIMIT }).map((_, i) => (
                        <div key={i} className={`w-2.5 h-2.5 rounded-full ${
                          i < freeUsesCount
                            ? 'bg-gray-300 dark:bg-gray-600'
                            : trialRemaining === 1 ? 'bg-amber-500' : 'bg-green-500'
                        }`} />
                      ))}
                    </div>
                    <span>
                      {trialRemaining} free generation{trialRemaining !== 1 ? 's' : ''} remaining
                      {trialRemaining === 1 && ' — last one!'}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 text-violet-800">
                    <i className="ri-sparkling-2-fill text-violet-500 text-base flex-shrink-0"></i>
                    <span className="font-semibold">Free trial complete!</span>
                    <button onClick={() => setShowUpgradeModal(true)} className="ml-auto px-3 py-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold rounded-lg cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity">
                      Upgrade →
                    </button>
                  </div>
                )}

                {/* Generate Button */}
                <button
                  data-tour="generate"
                  onClick={handleGenerate}
                  disabled={!prompt.trim() || isGenerating}
                  className={`w-full py-4 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    trialExhausted
                      ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90'
                      : 'bg-gradient-to-r from-purple-500 to-pink-600 hover:shadow-lg hover:shadow-purple-500/30'
                  }`}
                >
                  {isGenerating ? (
                    <><i className="ri-loader-4-line animate-spin mr-2"></i>Generating Image...</>
                  ) : trialExhausted ? (
                    <><i className="ri-sparkling-2-fill mr-2"></i>Upgrade to Generate More</>
                  ) : (
                    <><i className="ri-sparkling-fill mr-2"></i>Generate Image</>
                  )}
                </button>

                {/* Example Prompts */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                    <i className="ri-lightbulb-line mr-2 text-purple-500"></i>
                    Example Prompts
                  </h3>
                  <div className="flex gap-2 flex-wrap mb-4">
                    {Object.keys(EXAMPLE_PROMPTS).map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setActiveExampleCategory(cat)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          activeExampleCategory === cat
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-purple-100 dark:hover:bg-purple-900/20'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {EXAMPLE_PROMPTS[activeExampleCategory]?.map((ep, i) => (
                      <button
                        key={i}
                        onClick={() => setPrompt(ep)}
                        className="w-full text-left p-3 bg-gray-50 dark:bg-gray-900 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-700 dark:hover:text-purple-300 transition-all border border-transparent hover:border-purple-200 dark:hover:border-purple-800"
                      >
                        {truncateText(ep, 100)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Result */}
              <div className="space-y-6">
                {isGenerating ? (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border border-gray-200 dark:border-gray-700">
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                        <i className="ri-image-line text-white text-2xl"></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Creating Your Image</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{generationSteps[currentStep]}</p>
                    </div>
                    <div className="space-y-3">
                      {generationSteps.map((step, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                            i < currentStep ? 'bg-green-500' : i === currentStep ? 'bg-purple-500 animate-pulse' : 'bg-gray-200 dark:bg-gray-700'
                          }`}>
                            {i < currentStep
                              ? <i className="ri-check-line text-white text-xs"></i>
                              : i === currentStep
                              ? <i className="ri-loader-4-line text-white text-xs animate-spin"></i>
                              : <span className="w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full"></span>
                            }
                          </div>
                          <span className={`text-sm ${i <= currentStep ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-400 dark:text-gray-600'}`}>
                            {step}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-pink-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${((currentStep + 1) / generationSteps.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ) : generatedImage ? (
                  <>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <img
                          src={generatedImage}
                          alt={prompt}
                          className="w-full object-cover"
                          style={{ aspectRatio: aspectRatio === '16:9' ? '16/9' : aspectRatio === '9:16' ? '9/16' : aspectRatio === '4:3' ? '4/3' : '1/1' }}
                        />
                        <div className="absolute top-3 right-3 flex gap-2 flex-wrap justify-end">
                          <span className="px-2 py-1 bg-black/50 text-white text-xs rounded-lg backdrop-blur-sm">{selectedStyle}</span>
                          <span className="px-2 py-1 bg-black/50 text-white text-xs rounded-lg backdrop-blur-sm">{aspectRatio}</span>
                          <span className="px-2 py-1 bg-black/50 text-white text-xs rounded-lg backdrop-blur-sm">{quality}</span>
                          {lastUsedStyleTransfer && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-violet-600/80 to-fuchsia-600/80 text-white text-xs rounded-lg backdrop-blur-sm font-semibold">
                              <i className="ri-magic-line text-xs"></i>
                              {lastUsedStyleTransfer.type === 'preset'
                                ? `Style: ${lastUsedStyleTransfer.presetId}`
                                : 'Style Transfer'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="p-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">{prompt}</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => { if (trialExhausted) { setShowUpgradeModal(true); } else { setShowShareModal(true); } }}
                            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-medium rounded-lg hover:shadow-lg hover:shadow-purple-500/30 transition-all whitespace-nowrap"
                          >
                            <i className="ri-share-line mr-2"></i>Share & Export
                          </button>
                          <button
                            onClick={() => { if (trialExhausted) { setShowUpgradeModal(true); } else { showToastMsg('Image downloaded!'); } }}
                            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-all whitespace-nowrap"
                          >
                            <i className="ri-download-line mr-2"></i>Download
                          </button>
                        </div>
                        <button
                          onClick={handleGenerate}
                          className="w-full mt-2 px-4 py-2.5 border border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 font-medium rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all"
                        >
                          <i className="ri-refresh-line mr-2"></i>Regenerate
                        </button>
                        {/* Compare Variations */}
                        <button
                          onClick={generateVariation}
                          disabled={isGeneratingB}
                          className="w-full mt-2 px-4 py-2.5 border-2 border-dashed border-pink-300 dark:border-pink-700 text-pink-600 dark:text-pink-400 font-medium rounded-lg hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {isGeneratingB ? (
                            <><i className="ri-loader-4-line animate-spin mr-2"></i>Generating variation...</>
                          ) : (
                            <><i className="ri-layout-column-line mr-2"></i>Compare Variations<span className="ml-2 text-xs opacity-60 font-normal">Ctrl+K</span></>
                          )}
                        </button>

                        {/* Upscale toggle */}
                        <button
                          onClick={() => setShowUpscale(v => !v)}
                          className="w-full mt-2 px-4 py-2.5 flex items-center justify-center gap-2 border border-emerald-300 dark:border-emerald-700 text-emerald-600 dark:text-emerald-400 font-medium rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all text-sm"
                        >
                          <i className={`${showUpscale ? 'ri-arrow-up-s-line' : 'ri-zoom-in-line'}`}></i>
                          {showUpscale ? 'Hide Upscaler' : 'AI Upscale (2× / 4×)'}
                        </button>
                      </div>
                    </div>

                    {/* Upscale Panel */}
                    {showUpscale && (
                      <UpscalePanel
                        imageUrl={generatedImage}
                        darkMode={darkMode}
                        onUpscaleComplete={(mult) => showToastMsg(`Image upscaled to ${mult}×! Ready for download.`)}
                      />
                    )}
                  </>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-12 text-center border border-gray-200 dark:border-gray-700">
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <i className="ri-image-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Your Image Will Appear Here</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Enter a prompt and click generate to create stunning AI images</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* History Panel */}
        {showHistory && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowHistory(false)}></div>
            <div className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl z-50 overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-purple-500 to-pink-600 text-white p-6 z-10">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold">My Generations</h3>
                  <button onClick={() => setShowHistory(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
                <p className="text-sm text-white/80">{history.length} of 20 saved</p>
              </div>
              <div className="p-6">
                {history.length > 0 ? (
                  <>
                    <button
                      onClick={clearHistory}
                      className="w-full mb-4 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-sm font-medium"
                    >
                      <i className="ri-delete-bin-line mr-2"></i>Clear All History
                    </button>
                    <div className="space-y-4">
                      {history.map((item) => (
                        <div key={item.id} className="bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 transition-colors">
                          <div className="relative group">
                            <img src={item.imageUrl} alt={item.prompt} className="w-full h-40 object-cover" />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => restoreFromHistory(item)}
                                className="p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg hover:bg-gray-100 transition-colors"
                                title="Restore"
                              >
                                <i className="ri-refresh-line"></i>
                              </button>
                              <button
                                onClick={() => removeHistoryItem(item.id)}
                                className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                title="Remove"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-sm text-gray-900 dark:text-white mb-2 line-clamp-2">{item.prompt}</p>
                            <div className="flex flex-wrap gap-1 mb-2">
                              <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-xs rounded-full">{item.style}</span>
                              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-full">{item.aspectRatio}</span>
                              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-full">{item.quality}</span>
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{formatTimestamp(item.timestamp)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-history-line text-2xl text-gray-400"></i>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No generations yet. Create your first image to see it here!</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Share Modal */}
        {showShareModal && generatedImage && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowShareModal(false)}></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-6 text-white">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold">Share & Export</h3>
                    <button onClick={() => setShowShareModal(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                      <i className="ri-close-line text-xl"></i>
                    </button>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <img src={generatedImage} alt={prompt} className="w-full h-48 object-cover rounded-xl" />

                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Download</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={handleDownloadPNG} className="px-4 py-2.5 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-100 transition-colors">
                        <i className="ri-image-line mr-2"></i>PNG
                      </button>
                      <button onClick={handleDownloadJPG} className="px-4 py-2.5 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-100 transition-colors">
                        <i className="ri-image-2-line mr-2"></i>JPG
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Copy</p>
                    <div className="space-y-2">
                      <button onClick={handleCopyImageLink} className="w-full flex items-center gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <i className="ri-link-line text-purple-500"></i>Copy Image Link
                      </button>
                      <button onClick={handleCopyPrompt} className="w-full flex items-center gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <i className="ri-file-copy-line text-purple-500"></i>Copy Prompt
                      </button>
                      <button onClick={handleCopyEmbedCode} className="w-full flex items-center gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <i className="ri-code-s-slash-line text-purple-500"></i>Copy Embed Code
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Share On</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: 'twitter', label: 'Twitter', icon: 'ri-twitter-x-line' },
                        { id: 'facebook', label: 'Facebook', icon: 'ri-facebook-line' },
                        { id: 'pinterest', label: 'Pinterest', icon: 'ri-pinterest-line' }
                      ].map((s) => (
                        <button
                          key={s.id}
                          onClick={() => handleSocialShare(s.id)}
                          className="flex flex-col items-center gap-1 px-3 py-3 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <i className={`${s.icon} text-xl text-gray-700 dark:text-gray-300`}></i>
                          <span className="text-xs text-gray-600 dark:text-gray-400">{s.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Toast */}
        {showToast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl shadow-xl text-sm font-medium flex items-center gap-2">
            <i className="ri-check-line text-green-400 dark:text-green-600"></i>
            {toastMessage}
          </div>
        )}

        <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
        {showUpgradeModal && (
          <UpgradeTrialModal
            toolName="AI Image Generator"
            usedCount={freeUsesCount}
            onSignIn={() => { setShowUpgradeModal(false); setShowLoginModal(true); }}
            onClose={() => setShowUpgradeModal(false)}
          />
        )}
        <OnboardingTour darkMode={darkMode} />
        <KeyboardShortcutsLegend
          isOpen={showShortcuts}
          onClose={() => setShowShortcuts(false)}
          darkMode={darkMode}
        />
        {showComparison && generatedImage && generatedImageB && (
          <ComparisonView
            imageA={generatedImage}
            imageB={generatedImageB}
            promptA={prompt}
            promptB={promptB}
            style={selectedStyle}
            aspectRatio={aspectRatio}
            quality={quality}
            onPickA={() => setShowComparison(false)}
            onPickB={() => { setGeneratedImage(generatedImageB); setShowComparison(false); }}
            onClose={() => setShowComparison(false)}
            darkMode={darkMode}
          />
        )}
      </div>
    </div>
  );
}
